import { MAX_INT32_VALUE } from '@tom-ui/utils'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { YardPositionBlockRange } from '../models/yard-position-block-range.model'

export class YardPositionRangeSelectorStore {
  selectedBlockId?: string
  selectedBay?: string
  selectedRow?: string
  selectedTier?: string

  constructor(
    private readonly yardBlockStore: YardBlockStore,
    positionRange?: YardPositionBlockRange,
  ) {
    makeObservable(this, {
      selectedBlockId: observable,
      selectedBay: observable,
      selectedRow: observable,
      selectedTier: observable,

      setSelectedBlockId: action,
      setSelectedBay: action,
      setSelectedRow: action,
      setSelectedTier: action,
      reset: action,

      yardBlocks: computed,
      selectedBlock: computed,
      isSelectedBlockStandard: computed,
      bays: computed,
      rows: computed,
      tiers: computed,
      yardPositionBlockRange: computed,
    })

    this.applyPosition(positionRange)
  }

  applyPosition(positionRange?: YardPositionBlockRange) {
    if (!positionRange) {
      this.reset()
      return
    }

    this.setSelectedBlockId(positionRange.blockId)
    const bay = this.convertMinMaxToRangeString(
      this.bays,
      positionRange.minBay,
      positionRange.maxBay,
    )
    const row = this.convertMinMaxToRangeString(
      this.rows,
      positionRange.minRow,
      positionRange.maxRow,
    )
    const tier = this.convertMinMaxToRangeString(
      this.tiers,
      positionRange.minTier,
      positionRange.maxTier,
    )

    this.setSelectedBay(bay)
    this.setSelectedRow(row)
    this.setSelectedTier(tier)
  }

  setSelectedBlockId(id?: string) {
    if (this.selectedBlockId !== id) {
      this.selectedBlockId = id
      this.setSelectedBay()
      this.setSelectedRow()
      this.setSelectedTier()
    }
  }

  setSelectedBay(bay?: string) {
    if (this.selectedBay !== bay) {
      this.selectedBay = bay
    }
  }

  setSelectedRow(row?: string) {
    if (this.selectedRow !== row) this.selectedRow = row
  }

  setSelectedTier(tier?: string) {
    if (this.selectedTier !== tier) this.selectedTier = tier
  }

  reset() {
    this.setSelectedBlockId()
    this.setSelectedBay()
    this.setSelectedRow()
    this.setSelectedTier()
  }

  get yardPositionBlockRange(): YardPositionBlockRange | undefined {
    if (!this.selectedBlockId) return undefined

    const bayRange = this.getMinAndMaxFromPositionRangeString(this.bays, this.selectedBay)
    const rowRange = this.getMinAndMaxFromPositionRangeString(this.rows, this.selectedRow)
    const tierRange = this.getMinAndMaxFromPositionRangeString(this.tiers, this.selectedTier)

    return {
      blockId: this.selectedBlockId,
      minBay: bayRange.min,
      maxBay: bayRange.max,
      minRow: rowRange.min,
      maxRow: rowRange.max,
      minTier: tierRange.min,
      maxTier: tierRange.max,
    }
  }

  get yardBlocks() {
    return this.yardBlockStore.items.slice().sort((a, b) => a.name.localeCompare(b.name))
  }

  get selectedBlock() {
    return this.selectedBlockId ? this.yardBlockStore.yardBlocks[this.selectedBlockId] : undefined
  }

  get isSelectedBlockStandard() {
    const id = this.selectedBlockId
    if (!id) return false

    return this.selectedBlock?.type === 'Standard'
  }

  get bays() {
    if (!this.selectedBlockId) return []

    return this.selectedBlock?.bayNames ?? []
  }

  get rows() {
    if (!this.selectedBlockId) return []

    return this.selectedBlock?.rowNames ?? []
  }

  get tiers() {
    if (!this.selectedBlockId) return []

    return Array.from(
      { length: this.yardBlockStore.yardBlocks[this.selectedBlockId].maxTiers },
      (_, i) => i,
    ).map(x => (x + 1).toString())
  }

  private convertMinMaxToRangeString(labels: string[], min?: number, max?: number) {
    if (min == null || max === MAX_INT32_VALUE) return undefined
    if (max == null || max === min) return labels[min - 1]

    return `${labels[min - 1]}-${labels[max - 1]}`
  }

  private getMinAndMaxFromPositionRangeString(labels: string[], value?: string) {
    if (!value) return { min: 0, max: MAX_INT32_VALUE }

    const values = value.split('-')

    // Adding 1 to convert from 0-based index to 1-based position
    const min = labels.findIndex(l => l === values[0]) + 1
    const max = labels.findIndex(l => l === (values[1] || values[0])) + 1
    return { min, max }
  }
}
